import React, {useState, useMemo, useEffect, useContext} from 'react';
import styled from 'styled-components';

import Checkbox from 'components/forms/checkbox';
import Icon from 'components/icons/icon';

import theme, {bp, color} from 'settings/theme';

import {SearchContext} from 'services/searchService';


type Props = {
  paramType: SearchParamType
};


// Component
const Component: React.FC<Props> = (props) => {

  const search = useContext(SearchContext);

  const [collapsed, setCollapsed] = useState(true);

  const paramType = props.paramType;

  // Facet
  const facet = useMemo(() => search.facets![props.paramType.name], [search.facets![props.paramType.name]])


  // Options to show
  const optionsToShow = useMemo(() => {

    let options = facet?.options;
    options = options || [];

    if(collapsed){
      options = options.slice(0,5);
    }

    return options;

  }, [collapsed, facet])


  // Selected Values
  const selectedValues = useMemo(() => {

      if(paramType.type === 'string'){
        return search.params![paramType.name] ? [search.params![paramType.name]] : [];
      }
      else if(paramType.type === 'list'){
        return search.params![paramType.name] ? search.params![paramType.name].split('|') : [];
      }

  }, [search.params![paramType.name]]);


    // Checkbox Changed
    const handleCheckboxChange = (e:React.ChangeEvent<HTMLInputElement> , name:string) => {

      let newValue: string | null = '';

      if(paramType.type === 'string'){

        // Set the value or clear it
        newValue = search.params![paramType.name] === name ? newValue = null : newValue = name;

      }

      else if(paramType.type === 'list'){

        // If the parameter is already set
        if(search.params![paramType.name]){

          let list = search.params![paramType.name].split('|');

          // Remove it
          if(list.includes(name)){
            list = list.filter(item => item !== name);
          }

          // Add it
          else{
            list.push(name);
          }

          newValue = list.length > 0 ? list.join('|') : null;

        }

        // if the param is not already set
        else{
          newValue = name;
        }

      }

      search.setParam!(paramType.name, newValue ?? '');
      search.doSearch!();

    }

    // Handle Options Expanded
    const handleOptionsExpanded = () => {
      setCollapsed(false);
    };

    return (
      <StyledComponent className="searchCheckboxes">
        <>
          {optionsToShow && optionsToShow.length > 0 &&
            <div className="searchCheckboxes-rows">

              {optionsToShow.length > 0 && optionsToShow.map(option => (
                <div className={`searchCheckboxes-row ${selectedValues?.includes(option.name) ? `searchCheckboxes--selected` : ``}`} key={option.name}>
                  <div className="searchCheckboxes-row-left">
                    <Checkbox checked={selectedValues?.includes(option.name) ??  false} onChange={(e) => handleCheckboxChange(e, option.name)}>
                      {option.friendlyName}
                    </Checkbox>
                  </div>
                  {option.value !== undefined &&
                    <div className="searchCheckboxes-row-right">
                      ({option.value})
                    </div>
                  }
                </div>
              ))}

              {facet.options?.length > 5 && collapsed &&
                <div className="searchCheckboxes-collapsed-fade"></div>
              }
                
            </div>
          }
          {(optionsToShow === undefined || optionsToShow?.length === 0) &&
            <div>No Results Found</div>
          }

          {facet?.options?.length > 5 && collapsed &&
            <a onClick={handleOptionsExpanded} className="searchCheckboxes-collapsed-link">
              <span>Show <strong>{facet.options?.length - 5}</strong> more</span>
              <Icon iconName="chevron" />
            </a>
          }

        </>
      </StyledComponent>
    ) ;

};

export default Component;


// Styles
const StyledComponent = styled.div`

  display: block;

  .searchCheckboxes-rows{
    display: flex;
    flex-direction: column;
    position: relative;
  }

  .searchCheckboxes-row{

    display: flex;
    justify-content: space-between;

    &:not(:last-child){
      margin-bottom: 10px;
    }

  }

  .searchCheckboxes-row-left{
    display: flex;
    align-items: center;
  }

  .searchCheckboxes-row-right{
    display: flex;
    align-items: center;
    margin-left: 10px;
  }

  .searchCheckboxes--selected{
    
    .searchCheckboxes-row-right{
      color: ${color.primary};
      font-weight: bold;
    }

  }

  .searchCheckboxes-collapsed-fade{
    pointer-events: none;
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    height: 60px;
    background: linear-gradient(rgba(255 ,255 ,255 ,0), rgba(255 ,255 ,255 ,1));
  }

  .searchCheckboxes-collapsed-link{

    display: flex;
    align-items: center;
    justify-content: center;
    height: 30px;
    color: ${color.grey(30)};
    border-radius: 4px;
    background-color: rgba(0,0,0,0);
    transition: all 0.15s ease-out;

    span{
      margin-right: 6px;
    }

    .icon{

      width: 12px;
      height: 12px;

      svg{
        transition: all 0.15s ease-out;
        fill: ${color.grey(30)};
      }

    }

    &:hover, &:active{

      color: ${color.grey(10)};
      background-color: ${color.grey(95)};

      .icon svg{
        fill: ${color.grey(10)};
      }
    }

  }


`;



